/**
 * The Internal Audits reads for the `/audits` page and the audit record (13-32), on `useRecord`'s pattern (13-23):
 *
 *   - `useAudits(source?)`: every audit from ONE paged list read (`internalAuditService.listAudits`), plus, in practice
 *     mode, the practice audits this browser holds (`AUD-P<n>`, D-13-L-9). A failed read is an error, never an empty
 *     list; `reload()` is the person's to press. The source is injectable, so the preview renders with synthetic rows
 *     and no Graph (the `RecordsWaitingSource` pattern, 13-29).
 *   - `useAudit(ref)`: one audit and its sibling rows, a live `AUD-<id>` from Graph, a practice `AUD-P<n>` from the
 *     practice store (practice mode only).
 *
 * Both refetch on window focus at most once a minute (D-13-09-3). Loading is derived, never set in the effect.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useActionRuntime } from './useActionRuntime';
import { getAudit, listAudits } from '../services/internalAuditService';
import { listPracticeAudits, readPracticeAudit } from '../services/commitAdapter';
import type { SiblingRow } from '../services/recordService';
import type { RecordIO } from '../services/recordService';
import { errorStatus } from '../services/graphClient';
import { readAuditRef } from '../lib/auditQuery';
import type { AuditRow } from '../lib/auditVerbs';

export const AUDITS_REFETCH_MIN_MS = 60_000;

/** Where the list comes from. Default: the live list through the runtime's io. */
export interface AuditsSource {
  listLive: (() => Promise<AuditRow[]>) | null;
  listPractice: () => AuditRow[];
}

export interface UseAuditsResult {
  status: 'loading' | 'ready' | 'error';
  rows: AuditRow[];
  error: string | null;
  refreshing: boolean;
  /** The time the shown rows were read (the tabs' "today" is pinned to it). */
  readAt: number | null;
  reload: () => void;
}

function useFocusReload(reload: () => void, lastLoad: { current: number }) {
  useEffect(() => {
    const onFocus = () => {
      if (Date.now() - lastLoad.current >= AUDITS_REFETCH_MIN_MS) reload();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [reload, lastLoad]);
}

export function useAudits(override?: AuditsSource): UseAuditsResult {
  const { io, mode, siteError } = useActionRuntime();
  const [trigger, setTrigger] = useState(0);
  const lastLoad = useRef(0);
  const source = useMemo<AuditsSource>(
    () =>
      override ?? {
        listLive: io ? () => listAudits(io as RecordIO) : null,
        listPractice: () => (mode === 'practice' ? listPracticeAudits() : []),
      },
    [override, io, mode],
  );
  const key = `${io?.siteId ?? ''}|${mode}|${trigger}`;
  const [loaded, setLoaded] = useState<{ key: string; rows: AuditRow[]; error: string | null; at: number } | null>(null);

  useEffect(() => {
    if (!source.listLive && !override) return;
    let cancelled = false;
    lastLoad.current = Date.now();
    const run = async () => {
      const practice = source.listPractice();
      try {
        const live = source.listLive ? await source.listLive() : [];
        if (!cancelled) setLoaded({ key, rows: [...live, ...practice], error: null, at: Date.now() });
      } catch (err) {
        if (!cancelled) setLoaded({ key, rows: practice, error: err instanceof Error ? err.message : 'The audits could not be read.', at: Date.now() });
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [key, source, override]);

  const reload = useCallback(() => setTrigger((n) => n + 1), []);
  useFocusReload(reload, lastLoad);

  return useMemo<UseAuditsResult>(() => {
    const current = loaded && loaded.key === key ? loaded : null;
    if (!loaded) {
      const error = !source.listLive && !override && siteError ? siteError : null;
      return { status: error ? 'error' : 'loading', rows: [], error, refreshing: false, readAt: null, reload };
    }
    return {
      status: loaded.error ? 'error' : 'ready',
      rows: loaded.rows,
      error: loaded.error,
      refreshing: current === null,
      readAt: loaded.at,
      reload,
    };
  }, [loaded, key, source, override, siteError, reload]);
}

export type AuditStatus = 'loading' | 'ready' | 'not-found' | 'unknown-ref' | 'practice-only' | 'error';

export interface UseAuditResult {
  status: AuditStatus;
  row: AuditRow | null;
  siblings: SiblingRow[];
  error: string | null;
  refreshing: boolean;
  reload: () => void;
}

interface AuditLoaded {
  key: string;
  status: Exclude<AuditStatus, 'loading'>;
  row: AuditRow | null;
  siblings: SiblingRow[];
  error: string | null;
}

export function useAudit(ref: string | null | undefined): UseAuditResult {
  const { io, mode, siteError } = useActionRuntime();
  const [trigger, setTrigger] = useState(0);
  const lastLoad = useRef(0);
  const parsed = readAuditRef(ref);
  const canonical = parsed?.ref ?? null;
  const practice = parsed?.practice ?? false;
  const key = `${canonical ?? ''}|${io?.siteId ?? ''}|${mode}|${trigger}`;
  const ready = canonical !== null && (practice || io !== null);
  const [loaded, setLoaded] = useState<AuditLoaded | null>(null);

  useEffect(() => {
    if (!canonical || !ready) return;
    let cancelled = false;
    lastLoad.current = Date.now();
    const read = async (): Promise<Omit<AuditLoaded, 'key'>> => {
      if (practice) {
        if (mode !== 'practice') return { status: 'practice-only', row: null, siblings: [], error: null };
        const a = readPracticeAudit(canonical);
        return a ? { status: 'ready', row: a.row, siblings: a.siblings, error: null } : { status: 'not-found', row: null, siblings: [], error: null };
      }
      try {
        const r = await getAudit(io as RecordIO, canonical);
        return { status: 'ready', row: r.row, siblings: r.siblings, error: null };
      } catch (err) {
        if (errorStatus(err) === 404) return { status: 'not-found', row: null, siblings: [], error: null };
        return { status: 'error', row: null, siblings: [], error: err instanceof Error ? err.message : 'The audit could not be read.' };
      }
    };
    void read().then((l) => {
      if (!cancelled) setLoaded({ key, ...l });
    });
    return () => {
      cancelled = true;
    };
  }, [key, ready, canonical, practice, mode, io]);

  const reload = useCallback(() => setTrigger((n) => n + 1), []);
  useFocusReload(reload, lastLoad);

  return useMemo<UseAuditResult>(() => {
    if (!canonical) return { status: 'unknown-ref', row: null, siblings: [], error: null, refreshing: false, reload };
    const current = loaded && loaded.key === key ? loaded : null;
    const shown = current ?? (loaded && loaded.key.startsWith(`${canonical}|`) ? loaded : null);
    if (!shown) {
      const error = !practice && !io && siteError ? siteError : null;
      return { status: error ? 'error' : 'loading', row: null, siblings: [], error, refreshing: false, reload };
    }
    return { status: shown.status, row: shown.row, siblings: shown.siblings, error: shown.error, refreshing: current === null, reload };
  }, [loaded, key, canonical, practice, io, siteError, reload]);
}
